import { getIsMobile } from "./getIsMobile";
import { CLICK_HOTKEY_MAP } from "./hotkeys";

import type { ClickCombination, ClickHotkey, PlatformCombination, TouchGesture } from "./hotkeys";
import type { Symbol, KeyCombination } from "@corca/data";
import type { ModifierKey } from "react";

const SYMBOLS = "`~!@#$%^&*()-_=+[{]}\\|;:'\",<.>/?√‖⌈⌊⎰⌉⌋⎱∑∫";

const MODIFIER_KEYS: ModifierKey[] = [
  "Shift",
  "Alt",
  "AltGraph",
  "Control",
  "Meta",
  "CapsLock",
  "Fn",
  "FnLock",
  "NumLock",
  "ScrollLock",
  "Symbol",
  "SymbolLock",
];

export const isMacLike =
  typeof window !== "undefined" && /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);

export function keyMatcher(
  combination: KeyCombination | Symbol,
  event: KeyboardEvent | React.KeyboardEvent,
) {
  return buildKeyCombinations(event).includes(combination);
}

export function isModifierKey(event: KeyboardEvent | React.KeyboardEvent) {
  return MODIFIER_KEYS.some((key) => key === event.key);
}

/** Whether a mouse event carries the Mod modifier — ⌘ on Mac, Ctrl elsewhere. */
function isModClick(event: MouseEvent | React.MouseEvent) {
  return isMacLike ? event.metaKey : event.ctrlKey;
}

export function matchClick(event: MouseEvent | React.MouseEvent, key: ClickHotkey) {
  let combination: ClickCombination | PlatformCombination | TouchGesture | undefined =
    CLICK_HOTKEY_MAP[key];

  if (event.button !== 0) return false;
  // Ctrl+click is the native context-menu gesture on Mac, so it matches no click hotkey.
  if (isMacLike && event.ctrlKey) return false;

  if (combination && typeof combination !== "string") {
    if (getIsMobile()) {
      // Mobile is resolved ahead of `isMacLike` because iOS is both, and `displayHotkey`
      // resolves it the same way. A gesture with no touch stand-in keeps its desktop
      // form rather than going dark, so an attached keyboard and mouse still reach it.
      combination = combination.touch ?? combination.other;
    } else {
      combination = isMacLike ? combination.mac : combination.other;
    }
  }

  if (!combination) return false;

  switch (combination) {
    case "Click":
      return !isModClick(event) && !event.shiftKey && !event.altKey;
    case "Mod+Click":
      return isModClick(event) && !event.shiftKey && !event.altKey;
    case "Alt+Click":
      return !isModClick(event) && !event.shiftKey && event.altKey;
    case "Shift+Click":
      return !isModClick(event) && event.shiftKey && !event.altKey;
    case "Mod+Shift+Click":
      return isModClick(event) && event.shiftKey && !event.altKey;
    case "Mod+Alt+Click":
      return isModClick(event) && !event.shiftKey && event.altKey;
    case "Mod+Shift+Alt+Click":
      return isModClick(event) && event.shiftKey && event.altKey;
    default:
      return false;
  }
}

/**
 * Note the difference:
 * KeyCombination is layout-agnostic: Ctrl+Z should work in any layout
 * Symbol is layout-specific: we want to detect user pressing "$", not "Shift+Digit4"
 * ("$" could be in different places depending on layout)
 *
 * Preserve the order of modifiers: Mod -> Shift -> Alt
 */
export function buildKeyCombinations(event: KeyboardEvent | React.KeyboardEvent) {
  const combinations: (KeyCombination | Symbol)[] = [];
  const result = [];

  if (event.metaKey) {
    if (isMacLike) {
      result.push("Mod");
    } else {
      result.push("Meta");
    }
  }

  if (event.ctrlKey) {
    if (!isMacLike) {
      result.push("Mod");
    } else {
      result.push("Ctrl");
    }
  }

  if (event.shiftKey) {
    result.push("Shift");
  }

  if (event.altKey) {
    result.push("Alt");
  }

  let { code } = event;
  const { keyCode } = event;

  if (
    event.key.length === 1 &&
    /[a-z]/i.test(event.key) &&
    `Key${event.key.toUpperCase()}` !== code
  ) {
    // assume different layout like Dvorak
    code = `Key${event.key.toUpperCase()}`;
  }

  if ((keyCode === 229 || code === "") && ["Enter", "Backspace", "Tab"].includes(event.key)) {
    // fix android devices `onKeyDown` issue
    // it returns 229 for `keyCode` and "" for `code` on any key pressed
    code = event.key;

    // BUT it doesn't work for Spacebar key
    // so we check Spacebar key inside `handleInputChange` in `NodeKeyboardInput.tsx`
    // and call `splitInlineExpression` if needed
  }

  combinations.push([...result, code].join("+") as KeyCombination);

  if (SYMBOLS.includes(event.key)) {
    combinations.push(event.key as Symbol);
  }

  return combinations;
}
